import { useState, useEffect } from 'react';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { Login } from './pages/Login';
import { Register } from './pages/Register';
import { Dashboard } from './pages/Dashboard';
import { Generate } from './pages/Generate';
import { AudioLibrary } from './pages/AudioLibrary';
import { Audiobooks } from './pages/Audiobooks';
import { Podcasts } from './pages/Podcasts';
import { Settings } from './pages/Settings';
import { Billing } from './pages/Billing';
import { AdminDashboard } from './pages/admin/AdminDashboard';
import { AdminUsers } from './pages/admin/AdminUsers';
import { AdminCredentials } from './pages/admin/AdminCredentials';
import { AdminTiers } from './pages/admin/AdminTiers';
import { VerifyEmail } from './pages/VerifyEmail';
import { Listen } from './pages/Listen';
import { Layout } from './components/Layout';

type Page =
  | 'login'
  | 'register'
  | 'verify-email'
  | 'listen'
  | 'dashboard'
  | 'generate'
  | 'library'
  | 'audiobooks'
  | 'podcasts'
  | 'settings'
  | 'billing'
  | 'admin'
  | 'admin/users'
  | 'admin/credentials'
  | 'admin/tiers';

const ADMIN_PAGES: Page[] = ['admin', 'admin/users', 'admin/credentials', 'admin/tiers'];

function AppContent() {
  const { user, loading } = useAuth();
  const [page, setPage] = useState<Page>('dashboard');

  useEffect(() => {
    // Simple hash-based routing
    const handleHashChange = () => {
      // Share links use a real path (/listen/<token>) so the server can inject
      // the book name into link-preview meta tags; route them to the player.
      if (window.location.pathname.startsWith('/listen/')) {
        setPage('listen');
        return;
      }
      const raw = window.location.hash.slice(1) || 'dashboard';
      // The email verification link carries its token in the hash query:
      // /#verify-email?token=...
      const hash = raw.split('?')[0];
      if (['login', 'register', 'verify-email', 'listen', 'dashboard', 'generate', 'library', 'audiobooks', 'podcasts', 'settings', 'billing', 'admin', 'admin/users', 'admin/credentials', 'admin/tiers'].includes(hash)) {
        setPage(hash as Page);
      }
    };

    handleHashChange();
    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-ink-950">
        <div
          className="animate-spin rounded-full h-12 w-12"
          style={{ border: '2px solid #34343B', borderTopColor: 'var(--a-hi)' }}
        ></div>
      </div>
    );
  }

  // Email verification works both logged-in and logged-out (link opened from
  // the email client may not carry a session).
  if (page === 'verify-email') {
    return <VerifyEmail />;
  }

  // Public audiobook player (share link: /#listen?t=TOKEN) — works with or
  // without a session, so shared links open straight into the player.
  if (page === 'listen') {
    return <Listen />;
  }

  // Public routes
  if (!user) {
    if (page === 'register') {
      return <Register onNavigate={(p) => { window.location.hash = p; setPage(p as Page); }} />;
    }
    return <Login onNavigate={(p) => { window.location.hash = p; setPage(p as Page); }} />;
  }

  // Protected routes
  const navigate = (p: Page) => {
    window.location.hash = p;
    setPage(p);
  };

  // Admin routes are role-gated: non-admins are redirected to the dashboard
  // before any admin UI renders or admin API calls fire.
  const isAdmin = user.role === 'admin';
  const effectivePage: Page = ADMIN_PAGES.includes(page) && !isAdmin ? 'dashboard' : page;

  return (
    <Layout currentPage={effectivePage} onNavigate={navigate}>
      {effectivePage === 'dashboard' && <Dashboard onNavigate={navigate} />}
      {effectivePage === 'generate' && <Generate />}
      {effectivePage === 'library' && <AudioLibrary />}
      {effectivePage === 'audiobooks' && <Audiobooks />}
      {effectivePage === 'podcasts' && <Podcasts />}
      {effectivePage === 'settings' && <Settings />}
      {effectivePage === 'billing' && <Billing />}
      {effectivePage === 'admin' && isAdmin && <AdminDashboard />}
      {effectivePage === 'admin/users' && isAdmin && <AdminUsers />}
      {effectivePage === 'admin/credentials' && isAdmin && <AdminCredentials />}
      {effectivePage === 'admin/tiers' && isAdmin && <AdminTiers />}
    </Layout>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}
